import { LandingPageModalData } from './create-modal-landing-page/create-modal-landing-page.component';
import { normalizeConsentTextWithLabel } from './consent-link.util';
import { SuccessPageModalData } from './create-modal-success-page/create-modal-success-page.component';
import {
  findDeliveryEmailContent,
  findMmsContent,
  findRcsContent,
  findSmsDeliveryContent,
  hasEmailDeliveryApiContent,
  hasMmsDeliveryApiContent,
  hasRcsDeliveryApiContent,
  hasSmsDeliveryApiContent,
} from './sms-delivery.mapper';

export interface LandingPageValidationRuleApi {
  check: boolean;
  errorMessage: string;
  digits?: number;
}

export interface CommonWebsiteLandingPageValidationApi {
  isValidEmail: LandingPageValidationRuleApi;
  isValidUsNum: LandingPageValidationRuleApi;
  isValidDigits: LandingPageValidationRuleApi;
}

export interface CommonWebsiteLandingPageApi {
  logo?: string;
  title: string;
  body: string;
  form_input_label: string;
  from_input_label: string;
  form_input_type: 'email' | 'phone';
  validation: CommonWebsiteLandingPageValidationApi;
  button_text: string;
  button_color: string;
  consent_screen: {
    show_consent: boolean;
    contest_text: string;
    consent_text: string;
    consent_label: string;
    consent_url: string;
  };
}

export interface CommonWebsiteSuccessPageApi {
  logo_url?: string;
  use_success_page_logo_url: boolean;
  title: string;
  body: string;
  show_cta: boolean;
  button_text: string;
  button_color: string;
  button_url: string;
  resend_options: {
    show_resend: boolean;
    max_attempts: number;
    cooldown_text: string;
  };
}

export interface CommonWebsiteContentApi {
  platform: 'common_website';
  landing_page?: CommonWebsiteLandingPageApi;
  success_page?: CommonWebsiteSuccessPageApi;
}

export interface PublicCampaignShopApi {
  name: string;
  email: string;
  contact: string;
}

export interface PublicCampaignLocationApi {
  city?: string;
  street_address?: string;
  state?: string;
  country?: string;
  zip_code?: string;
}

/** GET /campaign/public/common-website/:campaignId */
export interface PublicCommonWebsiteContentApi {
  id: string;
  campaign_id: string;
  platform: 'common_website';
  landing_page?: CommonWebsiteLandingPageApi;
  success_page?: CommonWebsiteSuccessPageApi;
  shop?: PublicCampaignShopApi;
  location?: PublicCampaignLocationApi;
  status: boolean;
  created_at?: string;
  updated_at?: string;
  mms_config?: any;
  rcs_config?: any;
}

/** Formats shop location fields into a single mailing address line. */
export function formatPublicCampaignLocationAddress(
  location?: PublicCampaignLocationApi | null
): string {
  if (!location) {
    return '';
  }
  return [
    location.street_address,
    location.city,
    [location.state, location.zip_code].filter(Boolean).join(' '),
    location.country,
  ]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(', ');
}

export type CommonWebsiteSection = 'landing_page' | 'success_page';

function normalizeInputType(inputType: string): 'email' | 'phone' {
  const value = (inputType || '').toLowerCase();
  if (value === 'email') {
    return 'email';
  }
  return 'phone';
}

function modalInputTypeFromApi(formInputType: string | undefined): string {
  const value = (formInputType || '').toLowerCase();
  if (value === 'email') {
    return 'Email';
  }
  return 'Phone';
}

type LegacyValidationValue = boolean | LandingPageValidationRuleApi | undefined;

function parseValidationRule(
  value: LegacyValidationValue,
  defaultCheck: boolean,
  defaultErrorMessage: string,
  defaultDigits?: number
): LandingPageValidationRuleApi {
  if (typeof value === 'boolean') {
    return {
      check: value,
      errorMessage: value ? defaultErrorMessage : '',
      ...(defaultDigits !== undefined ? { digits: defaultDigits } : {}),
    };
  }

  if (value && typeof value === 'object') {
    return {
      check: value.check ?? defaultCheck,
      errorMessage: value.errorMessage?.trim() || defaultErrorMessage,
      ...(defaultDigits !== undefined
        ? { digits: value.digits ?? defaultDigits }
        : value.digits !== undefined
          ? { digits: value.digits }
          : {}),
    };
  }

  return {
    check: defaultCheck,
    errorMessage: defaultCheck ? defaultErrorMessage : '',
    ...(defaultDigits !== undefined ? { digits: defaultDigits } : {}),
  };
}

function buildLandingValidationPayload(
  data: LandingPageModalData,
  formInputType: 'email' | 'phone'
): CommonWebsiteLandingPageValidationApi {
  const isEmail = formInputType === 'email';
  const isPhone = formInputType === 'phone';

  return {
    isValidEmail: {
      check: isEmail && data.validateEmail,
      errorMessage:
        isEmail && data.validateEmail ? data.validateEmailErrorMessage?.trim() || '' : '',
    },
    isValidUsNum: {
      check: isPhone && data.validatePhone,
      errorMessage:
        isPhone && data.validatePhone ? data.validatePhoneErrorMessage?.trim() || '' : '',
    },
    isValidDigits: {
      check: isPhone && data.validateDigits,
      errorMessage:
        isPhone && data.validateDigits ? data.validateDigitsErrorMessage?.trim() || '' : '',
      digits: isPhone && data.validateDigits ? Number(data.digitLength) || 10 : 10,
    },
  };
}

export function mapLandingModalToApi(
  data: LandingPageModalData,
  logo?: string
): CommonWebsiteLandingPageApi {
  const formInputType = normalizeInputType(data.inputType);

  return {
    logo: logo || data.logo || '',
    title: data.title || '',
    body: data.subtitle || '',
    form_input_label: data.inputLabel || '',
    from_input_label: data.inputLabel || '',
    form_input_type: formInputType,
    validation: buildLandingValidationPayload(data, formInputType),
    button_text: data.buttonText || '',
    button_color: data.buttonColor || '#2F6DDE',
    consent_screen: {
      show_consent: data.showConsentBlock,
      contest_text: data.contestText || '',
      consent_text: data.consentText || '',
      consent_label: data.consentLabel || '',
      consent_url: data.consentUrl || '',
    },
  };
}

export function mapLandingApiToModal(api: CommonWebsiteLandingPageApi): LandingPageModalData {
  const isEmail = (api.form_input_type || '').toLowerCase() === 'email';
  const validation = api.validation;
  const emailRule = parseValidationRule(
    validation?.isValidEmail,
    isEmail,
    'Please enter a valid email address'
  );
  const usNumRule = parseValidationRule(
    validation?.isValidUsNum,
    !isEmail,
    'Please enter a valid US phone number'
  );
  const digitsRule = parseValidationRule(
    validation?.isValidDigits,
    !isEmail,
    'Please enter a valid 10-digit US number',
    10
  );

  return {
    logo: api.logo || '',
    title: api.title || '',
    subtitle: api.body || '',
    inputLabel: api.form_input_label || api.from_input_label || '',
    inputType: modalInputTypeFromApi(api.form_input_type),
    buttonText: api.button_text || '',
    buttonColor: api.button_color || '#2F6DDE',
    consentText: normalizeConsentTextWithLabel(
      api.consent_screen?.consent_text || '',
      api.consent_screen?.consent_label || ''
    ),
    consentLabel: api.consent_screen?.consent_label || '',
    consentUrl: api.consent_screen?.consent_url || '',
    contestText: api.consent_screen?.contest_text || '',
    validateEmail: isEmail && emailRule.check,
    validateEmailErrorMessage: emailRule.errorMessage,
    validatePhone: !isEmail && usNumRule.check,
    validatePhoneErrorMessage: usNumRule.errorMessage,
    validateDigits: !isEmail && digitsRule.check,
    digitLength: digitsRule.digits ?? 10,
    validateDigitsErrorMessage: digitsRule.errorMessage,
    showConsentBlock: api.consent_screen?.show_consent ?? true,
  };
}

export function mapSuccessModalToApi(
  data: SuccessPageModalData,
  logoUrl?: string
): CommonWebsiteSuccessPageApi {
  const showCta = Boolean(data.showCta);
  return {
    logo_url: logoUrl || data.logoUrl || '',
    use_success_page_logo_url: data.reuseLandingBrand,
    title: data.title || '',
    body: data.body || '',
    show_cta: showCta,
    button_text: showCta ? data.ctaText?.trim() || '' : '',
    button_color: data.ctaColor || '#2F6DDE',
    button_url: showCta ? data.ctaUrl?.trim() || '' : '',
    resend_options: {
      show_resend: data.showResend,
      max_attempts: data.maxAttempts ?? 3,
      cooldown_text: data.cooldownText || '',
    },
  };
}

export function mapSuccessApiToModal(api: CommonWebsiteSuccessPageApi): SuccessPageModalData {
  return {
    reuseLandingBrand: api.use_success_page_logo_url ?? false,
    logoUrl: api.logo_url || '',
    title: api.title || '',
    body: api.body || '',
    showCta: api.show_cta ?? Boolean(api.button_text?.trim() || api.button_url?.trim()),
    ctaText: api.button_text || '',
    ctaUrl: api.button_url || '',
    ctaColor: api.button_color || '#2F6DDE',
    showResend: api.resend_options?.show_resend ?? true,
    maxAttempts: api.resend_options?.max_attempts ?? 3,
    cooldownText: api.resend_options?.cooldown_text || '',
  };
}

/** True when the value is a non-empty http(s) URL that the CTA can actually navigate to. */
export function isValidCtaUrl(url?: string | null): boolean {
  const trimmed = (url || '').trim();
  if (!trimmed) {
    return false;
  }
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Single source of truth for whether the success-page CTA should render (preview + live page). */
export function shouldShowSuccessCta(
  data: Pick<SuccessPageModalData, 'showCta' | 'ctaUrl'>
): boolean {
  return Boolean(data.showCta) && isValidCtaUrl(data.ctaUrl);
}

export function hasLandingPageApiContent(
  landingPage?: CommonWebsiteLandingPageApi | null
): boolean {
  if (!landingPage) {
    return false;
  }
  return Boolean(landingPage.title?.trim() || landingPage.button_text?.trim());
}

export function hasSuccessPageApiContent(
  successPage?: CommonWebsiteSuccessPageApi | null
): boolean {
  if (!successPage) {
    return false;
  }
  return Boolean(successPage.title?.trim() || successPage.body?.trim());
}

export function findCommonWebsiteContent(contents: any): CommonWebsiteContentApi | null {
  if (!contents) {
    return null;
  }
  if (Array.isArray(contents)) {
    return contents.find((item: any) => item?.platform === 'common_website') || null;
  }
  if (contents.platform === 'common_website') {
    return contents;
  }
  return contents.common_website || null;
}

export interface PreviousLandingPageOption {
  id: string;
  label: string;
  data: LandingPageModalData;
}

export interface CampaignLandingPageSource {
  id: string;
  name: string;
  contents?: any;
}

/** Builds dropdown options from GET /campaign list items that have a configured landing page. */
export function mapCampaignsToPreviousLandingPageOptions(
  campaigns: CampaignLandingPageSource[],
  excludeCampaignId?: string | null
): PreviousLandingPageOption[] {
  if (!campaigns?.length) {
    return [];
  }

  const excludeId = excludeCampaignId?.trim() || '';
  const options: PreviousLandingPageOption[] = [];

  for (const campaign of campaigns) {
    if (!campaign?.id || (excludeId && campaign.id === excludeId)) {
      continue;
    }

    const commonWebsite = findCommonWebsiteContent(campaign.contents);
    const landingPage = commonWebsite?.landing_page;
    if (!hasLandingPageApiContent(landingPage)) {
      continue;
    }

    options.push({
      id: campaign.id,
      label: (campaign.name || '').trim() || 'Untitled campaign',
      data: mapLandingApiToModal(landingPage!),
    });
  }

  return options.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

export interface PreviousSuccessPageOption {
  id: string;
  label: string;
  data: SuccessPageModalData;
}

/** Builds dropdown options from GET /campaign list items that have a configured success page. */
export function mapCampaignsToPreviousSuccessPageOptions(
  campaigns: CampaignLandingPageSource[],
  excludeCampaignId?: string | null
): PreviousSuccessPageOption[] {
  if (!campaigns?.length) {
    return [];
  }

  const excludeId = excludeCampaignId?.trim() || '';
  const options: PreviousSuccessPageOption[] = [];

  for (const campaign of campaigns) {
    if (!campaign?.id || (excludeId && campaign.id === excludeId)) {
      continue;
    }

    const commonWebsite = findCommonWebsiteContent(campaign.contents);
    const successPage = commonWebsite?.success_page;
    if (!hasSuccessPageApiContent(successPage)) {
      continue;
    }

    options.push({
      id: campaign.id,
      label: (campaign.name || '').trim() || 'Untitled campaign',
      data: mapSuccessApiToModal(successPage!),
    });
  }

  return options.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

/** Returns a user-facing validation error, or null when the value is valid. */
export function validateLandingPageInput(value: string, data: LandingPageModalData): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return 'This field is required';
  }

  const isEmail = (data.inputType || '').toLowerCase() === 'email';

  if (isEmail) {
    if (data.validateEmail && !EMAIL_PATTERN.test(trimmed)) {
      return data.validateEmailErrorMessage?.trim() || 'Please enter a valid email address';
    }
    return null;
  }

  const numeric = digitsOnly(trimmed);

  if (data.validateDigits) {
    const expected = Number(data.digitLength) || 10;
    if (numeric.length !== expected) {
      return (
        data.validateDigitsErrorMessage?.trim() || `Please enter a valid ${expected}-digit number`
      );
    }
  }

  if (data.validatePhone) {
    const usLength = numeric.length === 10 || (numeric.length === 11 && numeric.startsWith('1'));
    if (!usLength) {
      return data.validatePhoneErrorMessage?.trim() || 'Please enter a valid US phone number';
    }
  }

  return null;
}

/** Query keys preserved across SMS campaign public page navigation. */
export const SMS_CAMPAIGN_PUBLIC_QUERY_KEYS = [
  'campaignId',
  'tracking_token',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export interface OptionalSettingsCompletionOptions {
  smsPlatformSelected?: boolean;
  emailPlatformSelected?: boolean;
}

export function isOptionalSettingsLandingComplete(contents: any): boolean {
  const commonWebsite = findCommonWebsiteContent(contents);
  return hasLandingPageApiContent(commonWebsite?.landing_page);
}

/** Shown in place of share / campaign URLs (success modal, overview) until the landing page exists. */
export const LANDING_PAGE_PENDING_SHARE_LINKS_HINT =
  'Share links will be available once the landing page is created. Set it up under Advance → Landing Page.';

/** Minimal campaign shape needed to decide whether its landing page exists. */
export interface CampaignLandingReadinessSource {
  landing_page_configured?: boolean | null;
  contents?: unknown;
}

/**
 * Single source of truth for whether a campaign's landing page exists, so share / landing URLs
 * can be shown. The server flag `landing_page_configured` wins when present; otherwise (editor
 * holding local contents, or a payload without the flag) it falls back to the saved contents.
 */
export function isCampaignLandingPageReady(
  campaign: CampaignLandingReadinessSource | null | undefined
): boolean {
  if (typeof campaign?.landing_page_configured === 'boolean') {
    return campaign.landing_page_configured;
  }
  return isOptionalSettingsLandingComplete(campaign?.contents);
}

/**
 * Returns `contents` with the common_website `landing_page` replaced (other entries and the
 * existing success_page kept), so readiness-gated URLs refresh in memory after a landing save.
 */
export function withCommonWebsiteLandingPage(
  contents: any,
  landingPage: CommonWebsiteLandingPageApi
): any {
  const existing = findCommonWebsiteContent(contents);
  const entry: CommonWebsiteContentApi = {
    ...existing,
    platform: 'common_website',
    landing_page: landingPage,
  };
  if (Array.isArray(contents)) {
    return existing
      ? contents.map((item: any) => (item === existing ? entry : item))
      : [...contents, entry];
  }
  if (contents && typeof contents === 'object' && contents.platform !== 'common_website') {
    return { ...contents, common_website: entry };
  }
  return [entry];
}

export function isOptionalSettingsSuccessComplete(contents: any): boolean {
  const commonWebsite = findCommonWebsiteContent(contents);
  return hasSuccessPageApiContent(commonWebsite?.success_page);
}

export function isOptionalSettingsDeliveryComplete(contents: any): boolean {
  return (
    hasSmsDeliveryApiContent(findSmsDeliveryContent(contents)) ||
    hasEmailDeliveryApiContent(findDeliveryEmailContent(contents)) ||
    hasMmsDeliveryApiContent(findMmsContent(contents)) ||
    hasRcsDeliveryApiContent(findRcsContent(contents))
  );
}

/** Mirrors optional-settings workflow: landing + success saved; delivery when SMS/email platforms are selected. */
export function isOptionalSettingsCompleteFromContents(
  contents: any,
  options: OptionalSettingsCompletionOptions = {}
): boolean {
  if (
    !isOptionalSettingsLandingComplete(contents) ||
    !isOptionalSettingsSuccessComplete(contents)
  ) {
    return false;
  }

  const needsDelivery = Boolean(options.smsPlatformSelected || options.emailPlatformSelected);
  if (!needsDelivery) {
    return true;
  }

  return isOptionalSettingsDeliveryComplete(contents);
}
